import { BookOpen, ChevronRight, ExternalLink } from 'feather-icons-react';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { useOrganization } from 'modules/organization/useOrganization';
import { PIXEL_CDN_URL, PIXEL_S2S_URL } from 'modules/pixel/constants';
import type { ConversionRule } from 'modules/pixel/pixelApiTypes';
import { usePixelConfig } from 'modules/pixel/usePixelConfig';

import { ROUTES } from 'routing/constants';

import { ctvTwMerge } from 'services/twMerge/ctvTwMerge';

import { CodeBlock } from './CodeBlock';
import { PIXEL_INTEGRATIONS, type IntegrationContext, type PixelIntegration } from './integrations';

interface IntegrationGuideProps {
  /** id of the integration to render (matches the integration registry). */
  integrationId: string;
}

/**
 * Page-level content for a single pixel integration (Manual JS, Shopify, Server-side, …).
 * Rendered by {@code PixelInstallation} for the platform in the URL.
 * The integration list itself comes from the {@link PIXEL_INTEGRATIONS} registry.
 */
export function IntegrationGuide({ integrationId }: IntegrationGuideProps) {
  const { currentAdvertiser, currentAgencyId } = useOrganization();
  const pixelCode = currentAdvertiser?.pixelCode ?? null;
  const { config } = usePixelConfig({
    advertiserId: currentAdvertiser?.id ?? null,
    autoFetch: true,
  });
  // The current agency. Used by S2S snippets to identify which credential set the Lambda
  // should use for DSP fan-out.
  const agencyId = currentAgencyId || null;
  const integration = useMemo(
    () => PIXEL_INTEGRATIONS.find((i) => i.id === integrationId),
    [integrationId],
  );

  if (!integration) {
    return <NotFoundCard />;
  }
  if (!pixelCode) {
    return <PixelMissingCard />;
  }

  const pixelScriptTag = `<script src="${PIXEL_CDN_URL}" id="${pixelCode}"></script>`;
  const ctx: IntegrationContext = {
    pixelCode,
    pixelScriptTag,
    s2sEndpoint: PIXEL_S2S_URL,
    cdnUrl: PIXEL_CDN_URL,
    agencyId: agencyId ?? null,
  };

  return (
    <div className="w-full min-w-0 min-h-full bg-white p-8 rounded-lg flex flex-col gap-6">
      <Header integration={integration} />
      <SetupCard integration={integration} ctx={ctx} />
      <PerRuleSnippets
        integration={integration}
        conversions={config?.conversions ?? []}
        ctx={ctx}
      />
    </div>
  );
}

function Header({ integration }: { integration: PixelIntegration }) {
  return (
    <div className="flex items-start justify-between gap-3 rounded-xl border border-primary-gray-200 bg-white p-6">
      <div className="flex items-start gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary-electric-50 text-primary-electric-700">
          {integration.icon ?? <BookOpen size={20} />}
        </div>
        <div>
          <h1 className="text-heading-xl font-semibold text-primary-gray-900">
            {integration.label}
          </h1>
          <p className="mt-1 text-base-sm text-primary-gray-500">{integration.description}</p>
        </div>
      </div>
      {integration.docsHref && (
        <a
          href={integration.docsHref}
          target="_blank"
          rel="noopener noreferrer"
          className="flex flex-shrink-0 items-center gap-1 rounded-lg border border-primary-gray-200 px-3 py-2 text-base-xs font-medium text-primary-gray-700 transition-colors hover:bg-primary-gray-50"
        >
          Read docs
          <ExternalLink size={12} />
        </a>
      )}
    </div>
  );
}

function SetupCard({
  integration,
  ctx,
}: {
  integration: PixelIntegration;
  ctx: IntegrationContext;
}) {
  return (
    <section className="rounded-xl border border-primary-gray-200 bg-white p-6">
      <h2 className="text-base font-semibold text-primary-gray-900">Setup</h2>
      <p className="mt-1 text-base-sm text-primary-gray-500">
        One-time install steps for {integration.label}.
      </p>
      <div className="mt-4">{integration.renderSetup(ctx)}</div>
    </section>
  );
}

function PerRuleSnippets({
  integration,
  conversions,
  ctx,
}: {
  integration: PixelIntegration;
  conversions: ConversionRule[];
  ctx: IntegrationContext;
}) {
  const snippets = useMemo(() => {
    if (!integration.buildSnippetForRule) return [];

    return conversions
      .map((rule) => ({ rule, snippet: integration.buildSnippetForRule!(rule, ctx) }))
      .filter(
        (entry): entry is { rule: ConversionRule; snippet: NonNullable<typeof entry.snippet> } =>
          entry.snippet !== null,
      );
  }, [conversions, ctx, integration]);

  const [expandedRule, setExpandedRule] = useState<string | null>(null);

  if (!integration.buildSnippetForRule) {
    return null;
  }

  if (snippets.length === 0) {
    return (
      <section className="rounded-xl border border-dashed border-primary-gray-300 bg-primary-gray-50 p-6 text-center">
        <h2 className="text-base font-semibold text-primary-gray-900">Per-conversion snippets</h2>
        <p className="mt-1 text-base-sm text-primary-gray-500">
          Define conversion rules under{' '}
          <Link
            to={ROUTES.PIXEL_CODE}
            className="font-semibold text-primary-electric-700 underline"
          >
            Pixel code
          </Link>{' '}
          to generate per-rule snippets here.
        </p>
      </section>
    );
  }

  return (
    <section className="rounded-xl border border-primary-gray-200 bg-white p-6">
      <h2 className="text-base font-semibold text-primary-gray-900">Per-conversion snippets</h2>
      <p className="mt-1 text-base-sm text-primary-gray-500">
        Each rule has a ready-to-paste snippet for {integration.label}. Click to expand.
      </p>
      <div className="mt-4 flex flex-col gap-2">
        {snippets.map(({ rule, snippet }) => {
          const key = ruleKey(rule);
          const isExpanded = expandedRule === key;

          return (
            <div
              key={key}
              className="overflow-hidden rounded-lg border border-primary-gray-200 bg-white"
            >
              <button
                type="button"
                onClick={() => setExpandedRule(isExpanded ? null : key)}
                className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left transition-colors hover:bg-primary-gray-50"
              >
                <div className="min-w-0 flex-1">
                  <p className="truncate text-base-sm font-medium text-primary-gray-900">
                    {rule.name}
                  </p>
                  <p className="truncate text-[11px] text-primary-gray-500">
                    {rule.category}
                    {rule.subcategory ? ` · ${rule.subcategory}` : ''}
                  </p>
                </div>
                <ChevronRight
                  size={16}
                  className={ctvTwMerge(
                    'flex-shrink-0 text-primary-gray-400 transition-transform',
                    isExpanded && 'rotate-90',
                  )}
                />
              </button>
              {isExpanded && (
                <div className="border-t border-primary-gray-100 bg-primary-gray-50 px-4 py-3">
                  {snippet.note && (
                    <p className="mb-2 text-base-xs text-primary-gray-600">{snippet.note}</p>
                  )}
                  <CodeBlock
                    label={snippet.label}
                    language={snippet.language}
                    code={snippet.code}
                    maxHeight="max-h-[400px]"
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}

function ruleKey(rule: ConversionRule): string {
  return `${rule.name}::${rule.category}::${rule.subcategory ?? ''}`;
}

function NotFoundCard() {
  return (
    <div className="flex h-64 items-center justify-center rounded-xl border border-primary-gray-200 bg-white">
      <p className="text-base-sm text-primary-gray-500">Unknown integration.</p>
    </div>
  );
}

function PixelMissingCard() {
  return (
    <div className="flex h-64 items-center justify-center rounded-xl border border-primary-gray-200 bg-white">
      <p className="text-base-sm text-primary-gray-500">
        Select an advertiser with a pixel code to view its integration snippets.
      </p>
    </div>
  );
}
